import React from 'react';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import FAQs from '../../../components/FAQs';
import { formatRoundFixed, getPricePercentageChange } from '../../../util';
import { Ratings } from '@benzinga/calendar-manager';
import { DelayedQuote } from '@benzinga/quotes-manager';
import { formatTickerExchange } from '../../../../utils/exchange';
import { AnalystRatingsSummary } from '@benzinga/calendar-manager';
import { formatPrice } from '@benzinga/calendars';
import { getCorrectIndefiniteArticle } from '@benzinga/utils';
import { formatDate } from './utils';

export enum stockTrend {
  downside = 'downside',
  fall = 'fall',
  rise = 'rise',
  upside = 'upside',
}

interface AnalystRatingsFAQProps {
  ratingsSummary: AnalystRatingsSummary;
  quote: DelayedQuote;
}

const AnalystRatingsFAQ: React.FC<AnalystRatingsFAQProps> = ({ quote, ratingsSummary }) => {
  const { t, i18n } = useTranslation('quote');
  const symbol = quote.symbol;
  const language = i18n.language;

  const FAQsDataSet = [
    {
      answer: getTargetPriceInfo(quote, ratingsSummary.ratings, t, language),
      question: t('Quote.FAQ.Questions.what-is-the-target-price-for', { quoteName: quote?.name, symbol }),
    },
    {
      answer: getRecentAnalystRating(quote, ratingsSummary.ratings, t),
      question: t('Quote.FAQ.Questions.AnalystRatings.what-is-the-most-recent', {
        quoteName: quote?.name,
        quoteSymbol: symbol,
      }),
    },
    {
      answer: getLastUpgrade(quote, ratingsSummary.ratings, t, language),
      question: t('Quote.FAQ.Questions.AnalystRatings.when-was-the-last-upgrade', {
        quoteName: quote?.name,
        quoteSymbol: symbol,
      }),
    },
    {
      answer: getLastDowngrade(quote, ratingsSummary.ratings, t, language),
      question: t('Quote.FAQ.Questions.AnalystRatings.when-was-the-last-downgrade', {
        quoteName: quote?.name,
        quoteSymbol: symbol,
      }),
    },
    {
      answer: getNextAnalystRating(quote, ratingsSummary.ratings, t, language),
      question: t('Quote.FAQ.Questions.AnalystRatings.when-is-the-next-analyst-rating-update', {
        quoteName: quote?.name,
        quoteSymbol: symbol,
      }),
    },
    {
      answer: getIsAnalystRatingCorrectInfo(quote, ratingsSummary.ratings, t),
      question: t('Quote.FAQ.Questions.AnalystRatings.is-the-analyst-rating-correct', {
        quoteName: quote?.name,
        quoteSymbol: symbol,
      }),
    },
  ];

  const title = `${quote?.name}`;

  return <FAQs FAQsDataSet={FAQsDataSet} title={title} />;
};

export default AnalystRatingsFAQ;

const formatActionCompany = (actionCompany: string, t: TFunction): string => {
  switch (actionCompany) {
    case 'Initiates Coverage On':
      return t('Quote.FAQ.Answers.AnalystRatings.initiated');
    case 'Upgrades':
      return t('Quote.FAQ.Answers.AnalystRatings.upgraded');
    case 'Downgrades':
      return t('Quote.FAQ.Answers.AnalystRatings.downgraded');
    case 'Maintains':
      return t('Quote.FAQ.Answers.AnalystRatings.maintained');
    case 'Reiterates':
      return t('Quote.FAQ.Answers.AnalystRatings.reiterated');
    default:
      return '';
  }
};

const getTargetPriceInfo = (
  quote: DelayedQuote,
  ratings: Ratings[],
  t: TFunction,
  language: string,
): React.ReactElement => {
  const fallbackValue = <p>{t('Quote.FAQ.Answers.AnalystRatings.no-price-target', { quoteName: quote?.name })}</p>;
  if (!ratings || ratings.length === 0) {
    return fallbackValue;
  }

  try {
    const lastRating = ratings[0];
    const tickerTrend = Number(lastRating.pt_current) > quote.lastTradePrice ? stockTrend.rise : stockTrend.fall;
    const tickerDirection =
      Number(lastRating.pt_current) > quote.lastTradePrice ? stockTrend.upside : stockTrend.downside;
    const date = DateTime.fromISO(lastRating.date).setLocale(language);
    const formattedPtCurrent = `$${Number(lastRating.pt_current).toFixed(2)}`;

    const exchange = formatTickerExchange(quote.bzExchange, quote.otcTier);
    const symbol = quote.symbol;
    const analyst = ratings[0].analyst;
    const reportDate = formatDate(date);
    const percentageChange = getPricePercentageChange(quote, lastRating);
    const ratingsCount = getCountRatings(ratings);

    return (
      <p>
        {t('Quote.FAQ.Answers.analyst-choice-answer', {
          analyst,
          date: reportDate,
          exchange,
          priceChange: percentageChange,
          ptCurrent: formattedPtCurrent,
          quoteName: quote?.name,
          ratingsLength: ratingsCount,
          symbol,
          tickerDirection,
          tickerTrend,
        })}
      </p>
    );
  } catch (error) {
    return fallbackValue;
  }
};

const getRecentAnalystRating = (quote: DelayedQuote, ratings: Ratings[], t: TFunction): React.ReactElement => {
  const fallbackValue = <p>{t('Quote.FAQ.Answers.AnalystRatings.no-analyst-for', { quoteName: quote?.name })}</p>;
  if (!ratings || ratings.length === 0) {
    return fallbackValue;
  }

  try {
    const lastRating = ratings[0];
    const formattedCurrentRating = lastRating.rating_current.toLowerCase();
    const quoteName = quote.name;
    const exchange = formatTickerExchange(quote.bzExchange, quote.otcTier);
    const symbol = quote.symbol;
    const analyst = lastRating.analyst;
    const actionCompany = formatActionCompany(lastRating.action_company ?? '', t);
    const currentRating = formattedCurrentRating;

    return (
      <p>
        {t('Quote.FAQ.Answers.AnalystRatings.the-latest-analyst-rating-for', {
          actionCompany,
          analyst,
          currentRating,
          exchange,
          quoteName,
          symbol,
        })}
      </p>
    );
  } catch (error) {
    return fallbackValue;
  }
};

const getNextAnalystRating = (
  quote: DelayedQuote,
  ratings: Ratings[],
  t: TFunction,
  language: string,
): React.ReactElement => {
  const fallbackValue = <p>{t('Quote.FAQ.Answers.AnalystRatings.no-next-analyst', { quoteName: quote?.name })}</p>;
  if (!ratings || ratings.length === 0) {
    return fallbackValue;
  }

  try {
    const lastRating = ratings[0];
    const date = DateTime.fromISO(lastRating.date).setLocale(language);
    const nextRatingDate = date.plus({ years: 1 });

    return (
      <p>
        {t('Quote.FAQ.Answers.AnalystRatings.analysis-arrive', {
          companyName: quote?.name,
          lastRatingDate: formatDate(date),
          nextRatingDateFormatted: formatDate(nextRatingDate),
        })}
      </p>
    );
  } catch (error) {
    return fallbackValue;
  }
};

const getIsAnalystRatingCorrectInfo = (quote: DelayedQuote, ratings: Ratings[], t: TFunction): React.ReactElement => {
  const fallbackValue = <p>{t('Quote.FAQ.Answers.AnalystRatings.no-next-analyst', { quoteName: quote?.name })}</p>;
  if (!ratings || ratings.length === 0) {
    return fallbackValue;
  }

  try {
    const lastRating = ratings[0];
    const current = Number(lastRating.pt_current);
    const prior = Number(lastRating.pt_prior);
    const type = current >= quote.lastTradePrice && quote.lastTradePrice >= prior ? 'within' : 'out of';
    const ptCurrent = `$${formatRoundFixed(current, 2)}`;
    const ptPrior = `$${formatRoundFixed(prior, 2)}`;
    const lastTradePrice = `$${formatRoundFixed(quote.lastTradePrice, 2)}`;

    const companyName = quote?.name;
    const symbol = quote.symbol;
    const actionCompany = formatActionCompany(lastRating.action_company ?? '', t);

    return (
      <p>
        {t('Quote.FAQ.Answers.AnalystRatings.subjective', {
          actionCompany,
          companyName,
          lastTradePriceValue: lastTradePrice,
          priceTargetCurrent: ptCurrent,
          priceTargetPrior: ptPrior,
          symbol,
          typeValue: type,
        })}
      </p>
    );
  } catch (error) {
    return fallbackValue;
  }
};

const getLastUpgrade = (
  quote: DelayedQuote,
  ratings: Ratings[],
  t: TFunction,
  language: string,
): React.ReactElement => {
  const fallbackValue = <p>{t('Quote.FAQ.Answers.AnalystRatings.no-last-upgrade', { quoteName: quote?.name })}</p>;
  if (!ratings || ratings.length === 0) {
    return fallbackValue;
  }

  try {
    const lastUpgrade = ratings.find(rating => rating.action_company === 'Upgrades') ?? null;

    if (!lastUpgrade) {
      return fallbackValue;
    }

    const date = lastUpgrade?.date ? DateTime.fromISO(lastUpgrade?.date).setLocale(language) : '';
    const formattedDate = date ? formatDate(date) : '';

    const companyName = quote.companyStandardName;
    const upgradeDate = formattedDate;
    const analyst = lastUpgrade.analyst;
    const priceTargetCurrent = lastUpgrade.pt_current ? formatPrice(lastUpgrade.pt_current) : 'N/A';
    const ratingPrior =
      getCorrectIndefiniteArticle(lastUpgrade.rating_prior) + ' ' + lastUpgrade.rating_prior.toLowerCase();

    return (
      <p>
        {t('Quote.FAQ.Answers.AnalystRatings.last-upgrade-happened', {
          analyst,
          companyName,
          priceTargetCurrent,
          ratingPrior,
          upgradeDate,
        })}
      </p>
    );
  } catch (error) {
    return fallbackValue;
  }
};

const getLastDowngrade = (
  quote: DelayedQuote,
  ratings: Ratings[],
  t: TFunction,
  language: string,
): React.ReactElement => {
  const fallbackValue = <p>{t('Quote.FAQ.Answers.AnalystRatings.no-last-downgrade', { quoteName: quote?.name })}</p>;
  if (!ratings || ratings.length === 0) {
    return fallbackValue;
  }

  try {
    const lastDowngrade = ratings.find(rating => rating.action_company === 'Downgrades') ?? null;

    if (!lastDowngrade) {
      return fallbackValue;
    }

    const date = DateTime.fromISO(lastDowngrade.date).setLocale(language);

    const companyName = quote.companyStandardName;
    const downgradeDate = formatDate(date);
    const analyst = lastDowngrade.analyst;
    const priceTargetPrior = lastDowngrade.pt_prior ? formatPrice(lastDowngrade.pt_prior) : 'N/A';
    const priceTargetCurrent = lastDowngrade.pt_current ? formatPrice(lastDowngrade.pt_current) : 'N/A';

    return (
      <p>
        {t('Quote.FAQ.Answers.AnalystRatings.last-downgrade', {
          analyst,
          companyName,
          downgradeDate,
          priceTargetCurrent,
          priceTargetPrior,
        })}
      </p>
    );
  } catch (error) {
    return fallbackValue;
  }
};

export const getCountRatings = (ratings: Ratings[]) => {
  const lastYear = DateTime.fromJSDate(new Date()).minus({ year: 1 }).toJSDate();
  const lastYearRatings = ratings.filter(rating => new Date(rating.date) > lastYear);
  return lastYearRatings.length;
};
